import { useId, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

const noticePdf = '/legal/eu-gewaehrleistung-de.pdf'
const noticeImage = '/legal/eu-gewaehrleistung-de.svg'
const consumerInformation = 'https://europa.eu/youreurope/garantien'

// Keep the Commission's artwork unchanged; online display must be in colour.
const NoticeContent = () => <>
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
    <Link href={noticePdf} target='_blank' rel='noopener' underline='always'>
      Offiziellen EU-Hinweis als PDF öffnen
    </Link>
    <Link href={consumerInformation} target='_blank' rel='noopener' underline='always'>
      Weitere Informationen zu Ihren Rechten (EU)
    </Link>
  </Box>
  <Box
    component='details'
    sx={{
      maxWidth: 760,
      mx: 'auto',
      mb: 2,
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      '& > summary': {
        cursor: 'pointer',
        color: 'primary.main',
        fontWeight: 600,
        minHeight: 44,
        boxSizing: 'border-box',
        p: 1.5,
      },
      '& > summary:focus-visible': {
        outline: '2px solid currentColor',
        outlineOffset: 2,
        borderRadius: 1,
      },
    }}
  >
    <summary>EU-Hinweis als Text lesen</summary>
    <Box sx={{ px: 2, pb: 2, fontSize: '1rem', lineHeight: 1.6, '& p': { my: 1.5 }, '& ul, & ol': { pl: 3 } }}>
      <Typography component='h3' variant='h6'>Gesetzliche Gewährleistung</Typography>
      <p>
        <strong>Mindestens zwei Jahre gesetzliche Gewährleistung der Vertragsmäßigkeit</strong>{' '}
        für Waren, die in der Europäischen Union verkauft werden.
      </p>
      <p>
        Verbraucherinnen und Verbraucher können ihre Rechte im Rahmen des gesetzlichen
        Gewährleistungsrechts geltend machen, z. B. wenn die Waren
      </p>
      <ul>
        <li>nicht der Beschreibung entsprechen,</li>
        <li>nicht bestimmungsgemäß funktionieren.</li>
      </ul>
      <p>
        <strong>Verkäufer haften</strong> für jede Vertragswidrigkeit, die zum Zeitpunkt
        der Lieferung der Waren bestand und innerhalb des Zeitraums der gesetzlichen
        Gewährleistung erkennbar wird. Verkäufer müssen in solchen Fällen Folgendes anbieten:
      </p>
      <ul>
        <li><strong>kostenlose Nachbesserung</strong> oder <strong>kostenlose Ersatzlieferung,</strong></li>
        <li>
          in bestimmten Fällen eine <strong>Preisminderung</strong> oder eine{' '}
          <strong>vollständige Erstattung des Kaufpreises.</strong>
        </li>
      </ul>
      <p>
        In einigen Ländern gilt ein längerer Zeitraum für die gesetzliche Gewährleistung.
        Für gebrauchte Waren kann ein kürzerer Zeitraum gelten, jedoch nicht weniger als ein Jahr.
      </p>
      <p>
        Für weitere Informationen zu Ihren Rechten in einem bestimmten Land scannen Sie
        den nachstehenden QR-Code oder fragen Sie den Verkäufer.
      </p>
      <p>
        <Link href={consumerInformation} target='_blank' rel='noopener' underline='always'>
          europa.eu/youreurope/garantien
        </Link>
      </p>
      <Typography component='h4' variant='subtitle1' sx={{ fontWeight: 600, mt: 2 }}>
        Was ist zu tun, wenn Sie vertragswidrige Waren erhalten?
      </Typography>
      <ol>
        <li>Melden Sie dem Verkäufer das Problem so bald wie möglich.</li>
        <li>Legen Sie einen Kaufnachweis vor, z. B. die Quittung, Rechnung oder einen Kontoauszug.</li>
      </ol>
      <p>
        Verkäufer und Hersteller können auch gewerbliche Garantien gewähren, die unabhängig
        von der gesetzlichen Gewährleistung gelten. Diese GARAN-Kennzeichnung zeigt beispielsweise,
        dass der Hersteller eine <strong>gewerbliche Haltbarkeitsgarantie</strong> ohne zusätzliche
        Kosten gewährt, die die gesamte Ware abdeckt.
      </p>
    </Box>
  </Box>
  <Box
    component='a'
    href={noticeImage}
    target='_blank'
    rel='noopener'
    aria-label='EU-Hinweis zur gesetzlichen Gewährleistung in voller Größe öffnen'
    sx={{ display: 'block', maxWidth: 760, mx: 'auto', backgroundColor: '#fff' }}
  >
    <Box
      component='img'
      src={noticeImage}
      width={595}
      height={842}
      alt='Offizieller EU-Hinweis zur gesetzlichen Gewährleistung. Der vollständige Wortlaut steht unter „EU-Hinweis als Text lesen“.'
      sx={{ display: 'block', width: '100%', height: 'auto' }}
    />
  </Box>
  <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5 }}>
    Zum Vergrößern den Hinweis öffnen. Zusätzliche Garantien richten sich nach den
    jeweiligen Garantiebedingungen; Ihre gesetzlichen Rechte bleiben unberührt.
  </Typography>
</>

const LegalGuaranteeNotice: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const [open, setOpen] = useState(false)
  const titleId = useId()

  if (!compact) return <NoticeContent />

  return <>
    <Box
      component='aside'
      aria-labelledby={titleId}
      sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper' }}
    >
      <Typography id={titleId} component='h2' variant='subtitle1' sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoOutlinedIcon fontSize='small' />
        Gesetzliche Gewährleistung
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: .5, mb: 1 }}>
        Ihre Rechte bei mangelhafter Ware.
      </Typography>
      <Link
        href={noticePdf}
        target='_blank'
        rel='noopener'
        underline='always'
        aria-haspopup='dialog'
        onClick={event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
          event.preventDefault()
          setOpen(true)
        }}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: .75, minHeight: 44, fontWeight: 600 }}
      >
        EU-Hinweis zur Gewährleistung ansehen
        <OpenInNewIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      </Link>
    </Box>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='md' aria-labelledby={`${titleId}-dialog`}>
      <DialogTitle id={`${titleId}-dialog`}>Gesetzliche Gewährleistung</DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 3 } }}>
        <NoticeContent />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Schließen</Button>
      </DialogActions>
    </Dialog>
  </>
}

export default LegalGuaranteeNotice
